/**
 * Fetches the list of product highlights for a specific group promo from the backoffice API.
 *
 * @param token - The authentication token for the API request.
 * @param promoId - The ID of the group promo.
 * @returns A promise that resolves to an array of product highlight data.
 * @throws Throws an error if the API request fails.
 */

import {
  BackofficeGetGroupedPromosProductHighlightResponse,
  QueryConfig,
} from '@diamond/shared/types';
import { apiInstance } from '@diamond/shared/utils';
import { useAuthStore } from '@diamond/sol-admin/features/auth';
import { queryOptions, useQuery } from '@tanstack/react-query';

export const getGroupPromoHighlightList = async (
  token: string,
  promoId: string
): Promise<BackofficeGetGroupedPromosProductHighlightResponse> => {
  if (!promoId) {
    throw new Error('Promo ID is required to fetch product highlights.');
  }
  const apiUrl = `backoffice/promo/${promoId}/product-highlight`;

  try {
    const { data: result } = await apiInstance(
      token
    ).get<BackofficeGetGroupedPromosProductHighlightResponse>(apiUrl);

    return result;
  } catch (error) {
    console.error(
      'Error fetching group promo highlight list via apiInstance:',
      error
    );
    throw error;
  }
};

export const BACKOFFICE_GROUP_PROMO_HIGHLIGHT_QUERY_KEY =
  'BackofficeGroupPromoHighlight';

function getGroupPromoHighlightListQueryOptions(
  promoId: string,
  access_token: string
) {
  return queryOptions({
    queryKey: [BACKOFFICE_GROUP_PROMO_HIGHLIGHT_QUERY_KEY, promoId],
    queryFn: () => getGroupPromoHighlightList(access_token, promoId),
    gcTime: 0,
    staleTime: 5 * 60 * 1000,
    retry: 3,
  });
}

/**
 * React Query hook for fetching and managing product highlight data for a group promo.
 *
 * @param promoId - The ID of the group promo. Changes to this ID will trigger a refetch
 * @param queryConfig - Optional configuration to override default query settings
 * @returns Query result object containing data, loading state, error state, and refetch function
 */
export function useGroupPromoHighlightList(
  promoId: string,
  queryConfig: QueryConfig<typeof getGroupPromoHighlightListQueryOptions> = {}
) {
  const { access_token } = useAuthStore();
  return useQuery({
    ...getGroupPromoHighlightListQueryOptions(promoId, access_token),
    ...queryConfig,
  });
}
